/* eslint-disable unicorn/no-null -- Cost APIs intentionally surface `null` when pricing data is unavailable. */

import { calculateCostCredits } from '../../constants/ai';
import type { AICostSummary, AIUsageSummary } from '../../types/ai';

export function buildUsageSummary(usage: {
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  reasoningTokens?: number | null;
}): AIUsageSummary {
  const promptTokens = Math.max(0, Math.trunc(usage.promptTokens ?? 0));
  const completionTokens = Math.max(0, Math.trunc(usage.completionTokens ?? 0));
  const reasoningTokensCandidate =
    usage.reasoningTokens ??
    (usage.totalTokens ? usage.totalTokens - (usage.promptTokens ?? 0) - (usage.completionTokens ?? 0) : null);
  const reasoningTokens =
    reasoningTokensCandidate === undefined || reasoningTokensCandidate === null
      ? undefined
      : Math.max(0, Math.trunc(reasoningTokensCandidate));
  const explicitTotal = typeof usage.totalTokens === 'number' ? Math.trunc(usage.totalTokens) : undefined;
  const totalTokens =
    typeof explicitTotal === 'number' && explicitTotal >= 0
      ? explicitTotal
      : promptTokens + completionTokens + (reasoningTokens ?? 0);

  return {
    promptTokens,
    completionTokens,
    totalTokens,
    ...(reasoningTokens === undefined ? {} : { reasoningTokens }),
  };
}

export function buildCostSummary(
  modelId: string,
  promptTokens: number,
  completionTokens: number
): AICostSummary | null {
  const safePrompt = Math.max(0, Math.trunc(promptTokens));
  const safeCompletion = Math.max(0, Math.trunc(completionTokens));
  if (safePrompt === 0 && safeCompletion === 0) {
    return null;
  }

  const { inputCost, outputCost, totalCost, pricingModel } = calculateCostCredits(modelId, safePrompt, safeCompletion);

  return {
    inputCostCents: inputCost,
    outputCostCents: outputCost,
    totalCostCents: totalCost,
    pricingModel,
  };
}

export function coerceUsageSummary(usage?: Partial<AIUsageSummary> | null): AIUsageSummary | null {
  if (!usage) {
    return null;
  }
  const summary = buildUsageSummary({
    promptTokens: usage.promptTokens ?? null,
    completionTokens: usage.completionTokens ?? null,
    totalTokens: usage.totalTokens ?? null,
    reasoningTokens: usage.reasoningTokens ?? null,
  });
  if (summary.promptTokens === 0 && summary.completionTokens === 0 && summary.totalTokens === 0) {
    return null;
  }
  return summary;
}

export function coerceCostSummary(cost?: Partial<AICostSummary> | null): AICostSummary | null {
  if (!cost) {
    return null;
  }
  const inputCostCents = Math.max(0, Math.trunc(cost.inputCostCents ?? 0));
  const outputCostCents = Math.max(0, Math.trunc(cost.outputCostCents ?? 0));
  const totalCostCents = Math.max(0, Math.trunc(cost.totalCostCents ?? inputCostCents + outputCostCents));
  const pricingModel = typeof cost.pricingModel === 'string' ? cost.pricingModel.trim() : '';
  if (inputCostCents === 0 && outputCostCents === 0 && totalCostCents === 0 && pricingModel.length === 0) {
    return null;
  }
  return {
    inputCostCents,
    outputCostCents,
    totalCostCents,
    pricingModel,
  };
}
